import { useAuth } from '@/app/_auth/auth-hooks'
import { DialogInputForm } from '@/app/_common/dialog-input-form'
import { ChatNotificationBanner } from '@/app/chat/_common/chat-notification-banner'
import { Button, ButtonProps } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { Message } from '@/hooks/api/_types'
import { useMessageFeedbackMutation } from '@/hooks/api/use-message-feedback-mutation'
import { useEmailMessageOutputMutation } from '@/hooks/api/use-send-message-as-email-mutation'
import { cn, copy } from '@/lib/utils'
import { copyImageToClipboard } from 'copy-image-clipboard'
import { saveAs } from 'file-saver'
import {
  CopyIcon,
  DownloadIcon,
  ExternalLinkIcon,
  MailCheckIcon,
  MailIcon,
  ThumbsUpIcon,
  XIcon,
} from 'lucide-react'
import React from 'react'

type FileDownloadOptions =
  | {
      textType: 'csv' | 'plain'
      content: string
      fileName?: string
      imgSrc?: never
    }
  | {
      imgSrc: string
      fileName?: string
      textType?: never
      content?: never
    }

type EmailOptions = {
  title?: string | null
  fileUrl?: string | null
  hidden?: boolean
}

type OpenInNewTabOptions = {
  onOpenInNewTab: () => void
}

type CopyOptions =
  | {
      text: string
      imgSrc?: never
    }
  | {
      text?: never
      imgSrc: string
    }

type FeedbackOptions = {
  hidden?: boolean
}

type MessageIconsOptions = {
  emailOptions?: EmailOptions
  copyOptions?: CopyOptions
  downloadOptions?: FileDownloadOptions
  feedbackOptions?: FeedbackOptions
  openInNewTabOptions?: OpenInNewTabOptions
}

type MessageIconsProps = Readonly<
  {
    workflowId: string
    messageId?: string
    feedback?: Message['feedback']
    className?: string
  } & MessageIconsOptions
>

function MessageIcons({
  workflowId,
  messageId,
  feedback,
  downloadOptions,
  emailOptions,
  copyOptions,
  openInNewTabOptions,
  feedbackOptions,
  className,
}: MessageIconsProps) {
  const refFeedbackDetails = React.useRef<HTMLDivElement | null>(null)
  const [selectedFeedback, setSelectedFeedback] = React.useState<'NEGATIVE' | null>(null)
  const { user } = useAuth()
  const emailMutation = useEmailMessageOutputMutation({
    onError: () => toast.error('Failed to send email'),
    onSuccess: ({ recipientEmail }) => toast.success(`Email sent to ${recipientEmail}`),
  })
  const feedbackMutation = useMessageFeedbackMutation({
    onError: () => toast.error('Failed to save feedback'),
    onSuccess: () => {
      toast.success('Feedback saved')
      setSelectedFeedback(null)
    },
  })

  const downloadFile = ({ textType, content, fileName, imgSrc }: FileDownloadOptions) => {
    try {
      if (imgSrc) {
        saveAs(imgSrc, fileName)
      } else if (textType) {
        const data = new Blob([content], {
          type: `text/${textType};charset=utf-8`,
        })
        saveAs(data, fileName)
      } else {
        throw new Error('DOWNLOAD_DATA_NOT_FOUND')
      }
    } catch (error) {
      console.error(error)
      toast.error('Download failed')
    }
  }

  const saveFeedback = ({
    feedbackType,
    feedbackContent = '',
    messageId,
  }: {
    feedbackType: NonNullable<Message['feedback']>
    feedbackContent?: string
    messageId: string
  }) => {
    return feedbackMutation.mutateAsync({
      workflowId,
      messageId,
      feedbackType,
      feedbackContent,
    })
  }

  const sendEmail = ({ title, fileUrl }: EmailOptions) => {
    return emailMutation.mutateAsync({
      workflowId,
      title: title ?? `Workflow - ${new Date().toLocaleDateString()}`,
      fileUrl,
    })
  }

  const copyToClipboard = async ({ text, imgSrc }: CopyOptions) => {
    const successMessage = 'Copied to clipboard'
    const errorMessage = 'Failed to copy message'
    try {
      if (text) {
        copy({
          text,
          successMessage,
          errorMessage,
        })
      } else if (imgSrc) {
        await copyImageToClipboard(imgSrc)
        toast.success(successMessage)
      } else {
        throw new Error('COPY_DATA_NOT_FOUND')
      }
    } catch (e) {
      console.error(e)
      toast.error(errorMessage)
    }
  }

  const showFeedbackDetails =
    (feedbackMutation.isError && feedback === 'NEGATIVE') || !!selectedFeedback

  const { showNegativeFeedback, showPositiveFeedback } = React.useMemo<{
    showPositiveFeedback: boolean
    showNegativeFeedback: boolean
  }>(() => {
    if (feedbackOptions?.hidden) {
      return {
        showNegativeFeedback: false,
        showPositiveFeedback: false,
      }
    }
    return {
      showPositiveFeedback: true,
      showNegativeFeedback: true,
    }
  }, [feedbackOptions?.hidden])
  React.useEffect(() => {
    if (showFeedbackDetails) {
      setTimeout(
        () => refFeedbackDetails.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
        150,
      )
    }
  }, [showFeedbackDetails])

  return (
    <div className={cn('w-full', className)}>
      <div className='flex w-full items-center gap-2 text-neutral-150'>
        {messageId && (
          <>
            {showPositiveFeedback && (
              <TooltipIconButton
                size={'xs'}
                disabled={feedback === 'POSITIVE'}
                onClick={() => saveFeedback({ feedbackType: 'POSITIVE', messageId })}
                titleString='Like'
                className='disabled:opacity-100'
              >
                <ThumbsUpIcon
                  strokeWidth={1.5}
                  className={cn(
                    feedback === 'POSITIVE' &&
                      selectedFeedback !== 'NEGATIVE' &&
                      'fill-mango-100 [&>path]:stroke-mango-300',
                  )}
                />
              </TooltipIconButton>
            )}
            {showNegativeFeedback && (
              <TooltipIconButton
                size={'xs'}
                onClick={() =>
                  setSelectedFeedback((prev) => (prev === 'NEGATIVE' ? null : 'NEGATIVE'))
                }
                titleString='Dislike'
                className='disabled:opacity-100'
              >
                <ThumbsUpIcon
                  strokeWidth={1.5}
                  className={cn(
                    'rotate-180',
                    (feedback === 'NEGATIVE' || selectedFeedback === 'NEGATIVE') &&
                      'fill-mango-100 [&>path]:stroke-mango-300',
                  )}
                />
              </TooltipIconButton>
            )}
          </>
        )}
        {copyOptions && (
          <TooltipIconButton
            size={'xs'}
            titleString='Copy'
            onClick={() => copyToClipboard(copyOptions)}
          >
            <CopyIcon strokeWidth={1.5} />
          </TooltipIconButton>
        )}
        {emailOptions && !emailOptions.hidden && user?.email && (
          <TooltipIconButton
            size={'xs'}
            titleString={`Email to ${user.email}`}
            disabled={emailMutation.isSuccess || emailMutation.isPending}
            onClick={() => sendEmail(emailOptions)}
          >
            {emailMutation.isSuccess ? (
              <MailCheckIcon strokeWidth={1.5} />
            ) : (
              <MailIcon strokeWidth={1.5} />
            )}
          </TooltipIconButton>
        )}
        {downloadOptions && (
          <TooltipIconButton
            size={'xs'}
            onClick={() => downloadFile(downloadOptions)}
            titleString='Download'
          >
            <DownloadIcon strokeWidth={1.5} />
          </TooltipIconButton>
        )}
        {openInNewTabOptions && (
          <TooltipIconButton
            size={'xs'}
            titleString='Open in new tab'
            onClick={() => openInNewTabOptions.onOpenInNewTab()}
          >
            <ExternalLinkIcon strokeWidth={1.5} />
          </TooltipIconButton>
        )}
      </div>
      {!feedbackOptions?.hidden && (
        <div className='flex w-full'>
          <div
            ref={refFeedbackDetails}
            className={cn(
              'h-full w-full overflow-hidden transition-[max-height,opacity] ease-in-out',
              showFeedbackDetails ? 'max-h-full opacity-100 md:max-h-[200px]' : 'max-h-0 opacity-0',
            )}
          >
            <ChatNotificationBanner
              variant='default'
              className='mt-4 flex flex-col items-start gap-4'
            >
              {messageId && (
                <>
                  <div className='flex w-full items-center justify-between'>
                    <div className='text-base font-normal'>Tell Us More:</div>
                    <TooltipIconButton
                      titleString='Cancel feedback'
                      onClick={() => setSelectedFeedback(null)}
                    >
                      <XIcon />
                    </TooltipIconButton>
                  </div>
                  <div className='flex flex-wrap gap-4'>
                    <FeedbackButton
                      disabled={feedbackMutation.isPending}
                      onClick={() => {
                        saveFeedback({
                          feedbackType: 'NEGATIVE',
                          feedbackContent: 'Not factually correct',
                          messageId,
                        })
                      }}
                    >
                      Not factually correct
                    </FeedbackButton>
                    <FeedbackButton
                      disabled={feedbackMutation.isPending}
                      onClick={() => {
                        saveFeedback({
                          feedbackType: 'NEGATIVE',
                          feedbackContent: "Didn't fully follow instructions",
                          messageId,
                        })
                      }}
                    >
                      Didn't fully follow instructions
                    </FeedbackButton>
                    <DialogInputForm
                      maxInputLength={1000}
                      onSubmit={({ inputValue }) => {
                        return saveFeedback({
                          feedbackType: 'NEGATIVE',
                          feedbackContent: inputValue,
                          messageId,
                        })
                      }}
                      title='Provide additional feedback'
                      descriptionHidden='Send us your additional feedback'
                      isLoading={feedbackMutation.isPending}
                      triggerNode={
                        <FeedbackButton disabled={feedbackMutation.isPending}>
                          More...
                        </FeedbackButton>
                      }
                    />
                  </div>
                </>
              )}
            </ChatNotificationBanner>
          </div>
        </div>
      )}
    </div>
  )
}

const FeedbackButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, ...props }, ref) => (
    <Button
      ref={ref}
      unstyled
      className='rounded-lg border border-neutral-300 p-2 text-sm font-normal tracking-normal text-neutral-500 transition-transform active:scale-[.975] disabled:pointer-events-none disabled:opacity-45'
      {...props}
    >
      {children}
    </Button>
  ),
)
FeedbackButton.displayName = 'FeedbackButton'

export { MessageIcons, type MessageIconsOptions, type MessageIconsProps }
